import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, DeleteOutline, Edit, OpenInNew } from '@mui/icons-material';
import apiService from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';
import { categoryLandingPath } from '../../utils/landingPaths';
import { SeoRichText } from '../SeoContent/CategorySeoContent';

const wordCount = (text) => (text ? text.trim().split(/\s+/).filter(Boolean).length : 0);

const MARKUP_HELP = 'Üres sor = új bekezdés · "## Alcím" · "- " kezdetű sorok = felsorolás · **félkövér**';

/**
 * Admin › Kategória-tartalmak: a kategória landing oldalak (/szolgaltatok/:slug) SEO-szövege és GYIK-je.
 * A kezdő szövegek a backend/prisma/data/categorySeoContent.js-ből töltődnek (csak üres mezőbe),
 * az itt mentett szöveget a seed már nem írja felül.
 */
export default function AdminCategoryContentPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // { id, name, slug, seoContent, seoFaq: [] }
  const [view, setView] = useState('edit');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedMessage, setSavedMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.get(API_ENDPOINTS.CATEGORIES.WITH_SEO);
      setCategories((response.data.categories || []).filter((c) => c.slug !== 'egyeb'));
    } catch {
      setError('Nem sikerült betölteni a kategóriákat.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEditor = (category) => {
    setEditing({
      id: category.id,
      name: category.name,
      slug: category.slug,
      seoContent: category.seoContent || '',
      seoFaq: Array.isArray(category.seoFaq) ? category.seoFaq.map((f) => ({ ...f })) : [],
    });
    setView('edit');
    setSaveError('');
  };

  const updateFaq = (index, field, value) => {
    setEditing((prev) => ({
      ...prev,
      seoFaq: prev.seoFaq.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const save = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const response = await apiService.put(API_ENDPOINTS.CATEGORIES.UPDATE_SEO_CONTENT(editing.id), {
        seoContent: editing.seoContent,
        seoFaq: editing.seoFaq,
      });
      if (!response.success) throw new Error(response.message);
      const saved = response.data.category;
      setCategories((prev) => prev.map((c) => (c.id === saved.id ? { ...c, ...saved } : c)));
      setSavedMessage(`Mentve: ${saved.name}`);
      setEditing(null);
    } catch (e) {
      setSaveError(e.message || 'Nem sikerült menteni.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Kategória-tartalmak</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 760 }}>
        A kategória landing oldalakon (pl. /szolgaltatok/fenykepeszek) a szolgáltatói lista alatt megjelenő szöveg és
        GYIK. A Google ebből érti meg, miről szól az oldal — az egyedi, hasznos szöveg javítja a helyezést. A
        kategória × megye oldalakon csak az első bekezdés jelenik meg.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {savedMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSavedMessage('')}>{savedMessage}</Alert>}

      <Stack spacing={1.5}>
        {categories.map((category) => {
          const words = wordCount(category.seoContent);
          const faqCount = Array.isArray(category.seoFaq) ? category.seoFaq.length : 0;
          return (
            <Card key={category.id} variant="outlined">
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography sx={{ fontWeight: 600 }}>{category.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{categoryLandingPath(category)}</Typography>
                </Box>
                <Chip
                  size="small"
                  color={words === 0 ? 'default' : words < 120 ? 'warning' : 'success'}
                  label={words === 0 ? 'Nincs szöveg' : `${words} szó`}
                />
                <Chip size="small" variant="outlined" label={`${faqCount} GYIK`} />
                <Tooltip title="Landing oldal megnyitása">
                  <IconButton size="small" component="a" href={categoryLandingPath(category)} target="_blank" rel="noopener">
                    <OpenInNew fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button size="small" variant="contained" startIcon={<Edit />} onClick={() => openEditor(category)}>
                  Szerkesztés
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Dialog open={Boolean(editing)} onClose={() => !saving && setEditing(null)} fullWidth maxWidth="md">
        {editing && (
          <>
            <DialogTitle>{editing.name} – landing szöveg</DialogTitle>
            <DialogContent dividers>
              <Tabs value={view} onChange={(e, v) => setView(v)} sx={{ mb: 2 }}>
                <Tab value="edit" label="Szerkesztés" />
                <Tab value="preview" label="Előnézet" />
              </Tabs>

              {view === 'edit' ? (
                <>
                  <TextField
                    label="Szöveg"
                    value={editing.seoContent}
                    onChange={(e) => setEditing((prev) => ({ ...prev, seoContent: e.target.value }))}
                    multiline
                    minRows={14}
                    fullWidth
                    helperText={`${MARKUP_HELP} — ${wordCount(editing.seoContent)} szó (ajánlott: 150–400)`}
                  />

                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Gyakori kérdések</Typography>
                  <Stack spacing={2}>
                    {editing.seoFaq.map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <TextField
                            label={`${index + 1}. kérdés`}
                            value={item.question}
                            onChange={(e) => updateFaq(index, 'question', e.target.value)}
                            size="small"
                            fullWidth
                          />
                          <TextField
                            label="Válasz"
                            value={item.answer}
                            onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                            size="small"
                            multiline
                            minRows={2}
                            fullWidth
                          />
                        </Stack>
                        <IconButton
                          aria-label="GYIK-tétel törlése"
                          onClick={() => setEditing((prev) => ({ ...prev, seoFaq: prev.seoFaq.filter((_, i) => i !== index) }))}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                  <Button
                    startIcon={<Add />}
                    sx={{ mt: 1.5 }}
                    disabled={editing.seoFaq.length >= 15}
                    onClick={() => setEditing((prev) => ({ ...prev, seoFaq: [...prev.seoFaq, { question: '', answer: '' }] }))}
                  >
                    Kérdés hozzáadása
                  </Button>
                </>
              ) : (
                <Box>
                  <SeoRichText text={editing.seoContent} />
                  {editing.seoFaq.filter((f) => f.question).map((item, index) => (
                    <Box key={index} sx={{ mt: 2 }}>
                      <Typography sx={{ fontWeight: 600 }}>{item.question}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.answer}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditing(null)} disabled={saving}>Mégse</Button>
              <Button variant="contained" onClick={save} disabled={saving}>
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Mentés'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
